define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var server = require('server');
    var serverUtil = require('util/server.util');
    var languageUtil = require('util/language.util');
    var deviceUtil = require('util/device.util');
    var config = require('config');
    var BaseModel = require('common/base.model');
    var user = require('user');
    var urlUtil = require('util/url.util');
    var storage = require('util/storage.util');
    var { dispatcher, getDeviceId } = require('bundle');

    /**
     * @class
     * @name AuthManager
     * @augments BaseModel
     */
    var AuthManager = BaseModel.extend({

        idAttribute: 'sessionToken',

        subscribe: function() {
            core.reply({
                'auth:isAdmin': this.isAdmin,
                'auth:action:login': this.login,
                'auth:action:quietLogout': this.quietLogout,
                'auth:action:register': this.register,
                'auth:get:tempToken': this._getTempTokenHandler
            }, this);
        },

        /**
         * Check admin role
         * @return {Boolean}
         */
        isAdmin: function() {
            return config.get('isAdminLoggedIn') && !urlUtil.getLocationParameterByName('hideAdminControls');
        },

        /**
         * Get temp auth token
         *
         * Can be used for getting temp token, in game launching for example.
         *
         * @param {Function} [callback]
         * @param {String} [serviceType]
         * @return {Deferred}
         */
        getTemporaryAuthenticationToken: function(callback, serviceType) {
            var loginState = $.Deferred();

            if (!window.Playtech.API?.auth?.getTemporaryAuthenticationToken) {
                loginState.reject();

                return loginState.promise();
            }

            window.Playtech.API.auth.getTemporaryAuthenticationToken(serviceType)
                .then(response => {
                    loginState.resolve(response);
                    callback?.(response);
                })
                .catch(error => {
                    loginState.reject(error);
                    callback?.(error);
                });

            return loginState.promise();
        },

        /**
         * Login user.
         *
         * Can be used only in model sync.
         * If you want to login user manually, you should use
         *
         * user.set({
         *    userName: 'johndoe',
         *    password: '123456'
         * })
         * authManager.login();
         *
         * @private
         * @param  {Object} options
         * @return {Deferred}
         */
        login: function(options) {
            var loginState = $.Deferred();

            if (!window.Playtech.API?.auth?.login) {
                loginState.reject();

                return loginState.promise();
            }

            var userName = user.get('userName');
            var password = user.get('password');
            var rememberMeLogin = user.get('rememberMeLogin');
            var fitToPlay = user.get('fitToPlay');
            var afterRegisterLogin = user.get('afterRegisterLogin');
            var loginMode = user.get('loginMode');

            storage.session.removeItem('fitToPlay');

            if (typeof fitToPlay === 'boolean') {
                storage.session.setItem('fitToPlay', fitToPlay);
            }

            const handleSuccess = (response) => {
                user.unset('password');
                loginState.resolve(response);
                options.success(response.data);
            };

            const handleError = (error) => {
                if (afterRegisterLogin && error.data?.errorCode === 301) {
                    const popupId = 'registration-recaptcha-popup';

                    Playtech.API.popup.showPopup('reCaptcha', {
                        id: popupId,
                        operation: 'login',
                        onReCaptchaTokenValidate: () => {
                            Playtech.API.popup.closePopups(popupId);
                            window.Playtech.API.auth.login(userName, password, rememberMeLogin, afterRegisterLogin, loginMode)
                                .then(handleSuccess)
                                .catch(handleError);
                        },
                        onClose: () => {
                            // no op
                        },
                        onReCaptchaTokenError: () => {
                            Playtech.API.popup.closePopups(popupId);
                        }
                    });
                }

                loginState.reject(error);
                options.error(error);
            };

            window.Playtech.API.auth.login(userName, password, rememberMeLogin, afterRegisterLogin, loginMode)
                .then(handleSuccess)
                .catch(handleError);

            return loginState.promise();
        },

        /**
         * Start logout action
         */
        logout: function() {
            this.logoutAction();
        },

        /**
         * Logout user
         * Can be called for direct user logout, without any popups/checks.
         */
        logoutAction: function() {
            window.Playtech.API?.auth?.logout?.();
        },

        /**
         * Logout user if session already expired
         */
        quietLogout: function() {
            window.Playtech.API?.auth?.logout?.({ isQuiet: true });
        },

        /**
         * Register user on IMS.
         * @private
         * @param  {Object} options
         * @return {Deferred}
         */
        register: function(options) {
            var def = $.Deferred();
            var lang = languageUtil.convertToIMS(user.get('lang'));
            var formData = user.get('formData');
            var registrationInfo = user.get('registrationInfo');
            var deviceType = urlUtil.getLocationParameterByName('deviceType');
            var signUpInfo = {
                signupDeviceType: deviceType ?? deviceUtil.alias,
                signupOsName: deviceUtil.os,
                signupOsVersion: deviceUtil.osVersion,
                signupDeliveryPlatform: deviceUtil.deliveryPlatform.slice(0, 50),
                signupDeviceBrowser: deviceUtil.browserName
            };
            var data = _.extend({}, formData, signUpInfo);

            if (registrationInfo?.playerHash) {
                data = _.extend({}, data, {
                    playerHash: registrationInfo?.playerHash
                });
            }

            if (!lang) {
                return $.Deferred().reject({
                    ID: -1,
                    data: {
                        errorCode: -1,
                        errorMessage: 'User can\'t be logged in',
                        message: 'Failed to get two letter language'
                    }
                });
            }

            data.language = lang.toUpperCase();

            if (data.userName && config.get('isUsernameInUppercase')) {
                data.userName = data.userName.toUpperCase();
            }

            if (!data.userName && !data.loginName) {
                user.set('loginMode', 'email');
            }

            if (registrationInfo?.externalToken) {
                user.set('loginMode', 'externalToken');
            }

            data.serial = getDeviceId();
            data.signupDeviceId = data.serial;

            var requests = [
                this._generateSubscriptionRequest(data),
                this._getDepositLimitRequest(data),
                this._getBetLimitRequest(data),
                this._getLossLimitRequest(data)
            ].filter(Boolean);

            var extendedOptions = _.extend({}, options, {
                externalToken: registrationInfo?.externalToken,
                personalId: data.personalId
            });

            //Batch goes here. We need only registration responses
            this._registrationRequest(data, requests, extendedOptions)
                .done(def.resolve)
                .fail((error) => {
                    options.error();
                    def.reject(error);
                });

            dispatcher.dispatch('registration:submitted');

            dispatcher.dispatch('registration:submitted');

            return def.promise();
        },

        _generateSubscriptionRequest: function(data) {
            var isDefault = Boolean(data.subscription);
            var isRegBuilder = Boolean(data.customSubscription);
            var params;

            if (!isDefault && !isRegBuilder) {
                return null;
            }

            if (isDefault) {
                var allChannels = {
                    subscription: {
                        channel: 'all',
                        type: 'promotional',
                        preference: data.subscription.value
                    }
                };
                params = this.formatContactPreferences(allChannels);

                delete data.subscription;
            }

            if (isRegBuilder) {
                params = _.clone(this.formatContactPreferences(data.customSubscription));

                delete data.customSubscription;
            }

            return {
                ID: 35513,
                description: 'Set user contact preferences',
                responses: [35514, 35515],
                contactPreferences: params
            };
        },

        _getDepositLimitRequest: function(data) {
            var limits = [];

            _.each({
                daily: 'daydepositlimit',
                weekly: 'weekdepositlimit',
                monthly: 'monthdepositlimit'
            }, function(limit, period) {
                Number(data[limit]) && limits.push({
                    amount: data[limit],
                    timePeriod: period
                });
                delete data[limit];
            });

            return limits.length
                ? {
                    ID: 89904,
                    description: 'Set deposit limits',
                    responses: [89908, 89900],
                    limits: limits
                }
                : null;
        },

        _getBetLimitRequest: function(data) {
            const limits = [];
            const action = data.betlimitaction;

            _.each({
                session: 'sessionbetlimit',
                day: 'daybetlimit',
                week: 'weekbetlimit',
                month: 'monthbetlimit'
            }, function(limit, period) {
                Number(data[limit]) && limits.push({
                    amount: data[limit],
                    timePeriod: period
                });
                delete data[limit];
            });

            delete data.betlimitaction;

            return limits.length
                ? {
                    ID: 35649,
                    description: 'Set bet limits',
                    responses: [35650, 35651],
                    action: action || 'message',
                    limits: limits
                }
                : null;
        },

        _getLossLimitRequest: function(data) {
            const action = data.losslimitaction;
            const limits = Object.entries({
                session: 'sessionlosslimit',
                day: 'daylosslimit',
                week: 'weeklosslimit',
                month: 'monthlosslimit'
            }).reduce((acc, [period, limit]) => {
                if (Number(data[limit])) {
                    acc.push({
                        amount: data[limit],
                        timePeriod: period
                    });
                }

                delete data[limit];

                return acc;
            }, []);

            delete data.losslimitaction;

            return limits.length
                ? {
                    ID: 35646,
                    description: 'Set loss limits',
                    responses: [35647, 35648],
                    action: action || 'message',
                    limits
                }
                : null;
        },

        _getTempTokenHandler: function() {
            var res = $.Deferred();

            this.getTemporaryAuthenticationToken(function(response) {
                res.resolve(response.data.token);
            });

            return res.promise();
        },

        _refineRegistrationData: function(data) {
            const getAddressDetails = function(val, key) {
                return [
                    'buildingNumber',
                    'propertyNumber',
                    'subbuilding',
                    'streetName',
                    'streetNumber',
                    'unitNumber'
                ].includes(key);
            };
            const addressDetails = _.pick(data, getAddressDetails);
            const dataMap = _.omit(data, getAddressDetails);
            const clientTypeByURL = urlUtil.getLocationParameterByName('clientType');
            const casinoName = config.get('casinoName');
            const clientSkin = config.get('clientSkin');
            const signupClientVersion = config.get('clientVersion');
            const signupUserAgent = deviceUtil.uaOrigin;

            dataMap.clientPlatform = dataMap.clientPlatform || deviceUtil.clientPlatform;
            dataMap.clientType = clientTypeByURL || dataMap.clienttype || config.get('clientType');
            delete dataMap.clienttype;

            return {
                ...casinoName && { casinoName },
                addressDetails: addressDetails,
                dataMap: {
                    ...casinoName && { casinoName },
                    ...clientSkin && { clientSkin },
                    ...signupClientVersion && { signupClientVersion },
                    ...signupUserAgent && { signupUserAgent },
                    ...dataMap
                }
            };
        },

        _registrationRequest: function(data, requests, options) {
            const def = $.Deferred();
            const token = user.get('signUpVerificationToken');
            const blackbox = this._getIovationBlackbox();
            const request = {
                ID: 31007,
                responses: [31008, 33006],
                description: 'Create Player Info (Register)',
                changeTimestamp: window.Playtech.API.formatter.getDateForIms(new Date()),
                ...this._refineRegistrationData(data),
                ...token && { token },
                ...blackbox && {
                    customData: [
                        {
                            'key': 'iovationBlackbox',
                            'value': blackbox
                        }
                    ]
                }
            };

            server.send(this.registrationRequestFilter(request))
                .done(function(response) {
                    const { playerCode, userId } = response.data;

                    this._afterRegistrationBatchRequest({ ...data, playerCode, userId }, requests)
                        .always(function(msg) {
                            this._successRegistration({ ...data, playerCode, userId }, options);
                            def.resolve(msg);
                        }.bind(this));
                }.bind(this))
                .fail(function(error) {
                    def.reject(error);
                });

            return def.promise();
        },

        registrationRequestFilter: function(request) {
            return request;
        },

        _afterRegistrationBatchRequest: function(data, requests) {
            return serverUtil.sendBatchRequest(
                requests, data.userId);
        },

        _successRegistration: function(data, options) {
            if (options.externalToken) {
                user.set({
                    userId: data.userId,
                    playerCode: data.playerCode,
                    userName: options.personalId,
                    password: options.externalToken,
                    registrationInfo: undefined
                });
            } else {
                user.set({
                    userName: data.loginName || data.userName || data.email,
                    password: data.password,
                    userId: data.userId,
                    playerCode: data.playerCode
                });
            }

            options.success();
        },

        /**
         * Get iovation blackbox
         * @return {String}
         */
        _getIovationBlackbox: function() {
            const iovation = window[window.io_global_object_name]?.getBlackbox?.() ?? {};

            return iovation.finished ? iovation.blackbox : null;
        },

        /**
         * Format contact preferences for subscriptions
         * @param {Object} subscriptions
         * @return {Array}
         */
        formatContactPreferences: function(subscriptions = {}) {
            const contactPreferences = [];
            const supportedChannels = [
                'SMS',
                'email',
                'phone',
                'directMail',
                'mobilePush',
                'internalMessage',
                'desktopCompanion'
            ];
            const allChannels = Object.values(subscriptions).reduce((acc, subscription) => {
                if (subscription.channel === 'all') {
                    acc.push(subscription);
                }

                return acc;
            }, []);

            if (allChannels.length) {
                allChannels.forEach(channel => {
                    supportedChannels.forEach(name => {
                        contactPreferences.push({
                            channel: name,
                            type: channel.type,
                            preference: channel.preference
                        });
                    });
                });
            }

            for (const key in subscriptions) {
                const subscription = subscriptions[key];

                if (subscription.channel !== 'all') {
                    const overridePref = contactPreferences.find(pref =>
                        pref.channel === subscription.channel && pref.type === subscription.type
                    );

                    if (overridePref) {
                        overridePref.preference = subscription.preference;
                    } else {
                        subscription.type.split(',').forEach(type => {
                            contactPreferences.push({ ...subscription, type: type.trim() });
                        });
                    }
                }
            }

            return contactPreferences;
        }
    });

    module.exports = AuthManager;
});
